import { useState, useEffect, useCallback, useMemo } from "react"
import type { Placement, ProfileType, Lob } from "../components/ats-fbo/types"
import type { CurrencyItem } from "../components/ats-fbo/fields/numeric-field"
import { mapOnboardingDetailsToPlacement, mapFormStateToOnboardingDecisions } from "../lib/placement-mappers"
import type { PlacementMapperLookups, ReverseMapperLookups } from "../lib/placement-mappers"
import type { PlacementFormState } from "../components/ats-fbo/types"
import type { SubmitResult } from "../api/onboarding-api.types"
import type { Result, ApiError } from "../types/result"
import { onboardingApi } from "../api/onboarding-api.client"
import { useAuth } from "../contexts/auth-context"
import { useTranslation } from "./use-translation"
import { env } from "../lib/env"
import { mapToProfileTypes, mapToLobs } from "../components/ats-fbo/decision-tables"
import { CODE_VALUE_GROUPS } from "../types/code-values"
import type { CodeItem } from "../types/code-values"
import { initialPlacements as mockPlacements, mockSourceConfig } from "../components/ats-fbo/mock-data"

// ---------------------------------------------------------------------------
// Hook deps & result
// ---------------------------------------------------------------------------

interface UsePlacementsDeps {
  onboardingId: string | null
  /** Consumer override — the human-readable record id shown in the drawer header (e.g. "ATS-12345", "CR-42"). Falls back to "{abbreviated sourceType}-{sourceId}". */
  idDisplayText: string | null
  /** Consumer override — falls back to the API's sourceType verbatim (e.g. "ATS") */
  sourceName: string | null
  /** Consumer override — the noun for the source record (e.g. "placement", "request") */
  recordNoun: string | null
  /** Consumer override — falls back to capitalize(recordNoun) + " Details" */
  title: string | null
  isCodeValuesReady: boolean
  getCodeValueCode: (id: number, groupName: string) => string
  getCodeValueIdByCode: (code: string, groupName: string) => number | null
  getCodeValues: (groupName: string, required?: boolean) => CodeItem[]
}

interface UsePlacementsResult {
  placement: Placement | null
  idDisplayText: string
  recordNoun: string
  title: string
  sourceName: string
  isLoading: boolean
  loadError: string | null
  profileTypes: ProfileType[]
  lobs: Lob[]
  currencies: CurrencyItem[]
  submitPlacement: (form: PlacementFormState) => Promise<Result<SubmitResult>>
}

function getLoadErrorMessage(error: ApiError): string {
  if (error.type === "not-found") return "This record could not be found."
  if (error.type === "unauthorized") return "Unable to load. Please try closing and reopening."
  return "Something went wrong. Please try again later."
}

/** Extracts uppercase initials from a camelCase string: "ClientRequest" → "CR", "ATS" → "ATS" */
function abbreviate(sourceType: string): string {
  const initials = sourceType.replace(/[^A-Z]/g, "")
  return initials || sourceType.toUpperCase()
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

const MOCK_CURRENCIES: CurrencyItem[] = [
  { code: "CAD", text: "Canadian Dollar" },
  { code: "USD", text: "US Dollar" },
]

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function usePlacements(deps: UsePlacementsDeps): UsePlacementsResult {
  const { t } = useTranslation()
  const { isAuthenticated } = useAuth()
  const [placement, setPlacement] = useState<Placement | null>(null)
  const [derivedRecordNoun, setDerivedRecordNoun] = useState<string>("")
  const [derivedTitle, setDerivedTitle] = useState<string>("")
  const [derivedSourceName, setDerivedSourceName] = useState<string>("")
  const [derivedIdDisplayText, setDerivedIdDisplayText] = useState<string>("")
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Derive profileTypes and lobs (decision tables in mock mode, code values in API mode)
  const profileTypes = useMemo(() => {
    if (env.useMockData) return mapToProfileTypes()
    const items = deps.getCodeValues(CODE_VALUE_GROUPS.PROFILE_TYPE)
    if (items.length === 0) return mapToProfileTypes()
    // Filter to worker profile types only (parentId 3 = Worker group in usr.CodeProfileGroup).
    const workerItems = items.filter((i) => i.parentId === 3 && i.parentGroup === CODE_VALUE_GROUPS.PROFILE_GROUP)
    return mapToProfileTypes(workerItems.map((i) => ({ id: i.id, code: i.code, text: i.text })))
  }, [deps.isCodeValuesReady, deps.getCodeValues])

  const lobs = useMemo(() => {
    if (env.useMockData) return mapToLobs()
    const items = deps.getCodeValues(CODE_VALUE_GROUPS.LINE_OF_BUSINESS)
    return items.length > 0
      ? mapToLobs(items.map((i) => ({ id: i.id, code: i.code, text: i.text })))
      : mapToLobs()
  }, [deps.isCodeValuesReady, deps.getCodeValues])

  const currencies = useMemo(() => {
    if (env.useMockData) return MOCK_CURRENCIES
    const items = deps.getCodeValues(CODE_VALUE_GROUPS.CURRENCY)
    return items.length > 0
      ? items.map((i) => ({ code: i.code, text: i.text }))
      : MOCK_CURRENCIES
  }, [deps.isCodeValuesReady, deps.getCodeValues])

  const mapperLookups = useMemo((): PlacementMapperLookups => ({
    getCodeValueCode: deps.getCodeValueCode,
  }), [deps.isCodeValuesReady, deps.getCodeValueCode])

  // Load placement: mock mode uses first mock placement, API mode fetches and maps
  useEffect(() => {
    if (env.useMockData) {
      const sourceName = deps.sourceName ?? mockSourceConfig.sourceName
      const recordNoun = deps.recordNoun ?? mockSourceConfig.recordNoun
      const title = deps.title ?? t("recordNounDetails", { recordNoun: capitalize(recordNoun) })
      setDerivedRecordNoun(recordNoun)
      setDerivedTitle(title)
      setDerivedSourceName(sourceName)
      // Special mock IDs for testing shell states
      if (deps.onboardingId === "__loading") return // stays loading indefinitely
      if (deps.onboardingId === "__error") {
        setLoadError("Mock error: failed to load placement.")
        setIsLoading(false)
        return
      }
      const found = deps.onboardingId
        ? mockPlacements.find((p) => p.atsId === deps.onboardingId) ?? mockPlacements[0]
        : mockPlacements[0]
      setDerivedIdDisplayText(deps.idDisplayText ?? found?.atsId ?? "")
      setPlacement(found ?? null)
      setIsLoading(false)
      return
    }
    if (!isAuthenticated || !deps.onboardingId || !deps.isCodeValuesReady) return
    setIsLoading(true)
    setLoadError(null)
    onboardingApi
      .getOnboarding(deps.onboardingId)
      .then((result) => {
        if (!result.ok) {
          setLoadError(getLoadErrorMessage(result.error))
          setIsLoading(false)
          return
        }
        const details = result.data
        const idDisplayText = deps.idDisplayText ?? `${abbreviate(details.sourceType)}-${details.sourceId}`
        const sourceName = deps.sourceName ?? details.sourceType
        const recordNoun = deps.recordNoun ?? sourceName.toLowerCase()
        const title = deps.title ?? t("recordNounDetails", { recordNoun: capitalize(recordNoun) })
        setDerivedIdDisplayText(idDisplayText)
        setDerivedSourceName(sourceName)
        setDerivedRecordNoun(recordNoun)
        setDerivedTitle(title)
        setPlacement(mapOnboardingDetailsToPlacement(details, mapperLookups, idDisplayText))
        setIsLoading(false)
      })
  }, [isAuthenticated, deps.onboardingId, deps.idDisplayText, deps.sourceName, deps.recordNoun, deps.title, deps.isCodeValuesReady])

  const reverseLookups = useMemo((): ReverseMapperLookups => ({
    getCodeValueIdByCode: deps.getCodeValueIdByCode,
  }), [deps.getCodeValueIdByCode])

  const submitPlacement = useCallback(async (form: PlacementFormState): Promise<Result<SubmitResult>> => {
    if (!deps.onboardingId) throw new Error("submitPlacement called without onboardingId")
    const decisions = mapFormStateToOnboardingDecisions(form, reverseLookups)
    return onboardingApi.submitOnboarding(deps.onboardingId, decisions)
  }, [deps.onboardingId, reverseLookups])

  return {
    placement,
    idDisplayText: derivedIdDisplayText,
    recordNoun: derivedRecordNoun,
    title: derivedTitle,
    sourceName: derivedSourceName,
    isLoading,
    loadError,
    profileTypes,
    lobs,
    currencies,
    submitPlacement,
  }
}
